import { ArrowLeft, ArrowRight } from "@phosphor-icons/react";
import { useEffect } from "react";
import { IntegrationSnippets, integrationForKind } from "../docs/integrations";
import { InlineCode } from "../docs/primitives";
import { trackButtonPressed } from "../../analytics/tracking";
import type { AgentKind } from "../../agents";
import { AgentTrafficCard } from "./AgentTrafficCard";
import { StepBody } from "./StepBody";
import { SupportLink, TroubleshootingChecklist } from "./TroubleshootHelp";
import { useAgentUsedPoll } from "./useAgentUsedPoll";
import { EDITOR_WAIT_MS, TERMINAL_WAIT_MS, useStalled } from "./waiting";
import { WizardCta } from "./WizardCta";
import { WizardFrame } from "./WizardFrame";

/* Connect — the reader's personalized snippet, and the wait for their first request.
 * `kind` comes from step 1: SnippetContext's agent is briefly null and flashed the generic snippet. */
export function ConnectStep({
  kind,
  agentId,
  onContinue,
  onBack,
  onChangeType,
  onSkip,
  onUseCli,
  connected = false,
  ctaLabel = "Continue",
  standalone = false,
}: {
  kind: AgentKind;
  agentId: string;
  /** Advance the wizard (or close, for the drawer's "Done"). */
  onContinue: () => void;
  /** Back to Set up. Omitted by the drawer, which is not a wizard step. */
  onBack?: () => void;
  /** Omit to hide the "Not your agent type?" link (the drawer, where the type is fixed). */
  onChangeType?: () => void;
  /** Leave for the dashboard without waiting, offered once the wait has run long. */
  onSkip?: () => void;
  /** Back to the CLI, for a reader who picked the snippet and changed their mind. */
  onUseCli?: () => void;
  /** Traffic was already seen on an earlier visit — don't auto-advance them off the
   *  snippet they came back to re-copy. */
  connected?: boolean;
  ctaLabel?: string;
  /** True where this is the whole flow, not a wizard step (reconnect drawer). */
  standalone?: boolean;
}) {
  if (standalone)
    return (
      <StandaloneConnect
        kind={kind}
        agentId={agentId}
        onContinue={onContinue}
        ctaLabel={ctaLabel}
        onChangeType={onChangeType}
      />
    );

  return (
    <WizardConnect
      kind={kind}
      agentId={agentId}
      onContinue={onContinue}
      onBack={onBack}
      onSkip={onSkip}
      onUseCli={onUseCli}
      connected={connected}
    />
  );
}

/* Connect, for the kinds configured by hand. No forward control by design: the
 * step advances itself once the gateway sees this agent's traffic. The waiting lives here
 * rather than on Verify — this is the screen the reader sits on while nothing arrives, so
 * this is where the help and the way out belong (OPSS-333). */
function WizardConnect({
  kind,
  agentId,
  onContinue,
  onBack,
  onSkip,
  onUseCli,
  connected = false,
}: {
  kind: AgentKind;
  agentId: string;
  onContinue: () => void;
  onBack?: () => void;
  onSkip?: () => void;
  onUseCli?: () => void;
  connected?: boolean;
}) {
  const integration = integrationForKind(kind);
  const { used } = useAgentUsedPoll(agentId);
  /* Codex is terminal work — paste the prompt, let it rewrite config.toml, `codex login` —
     so it gets the same budget as the CLI handoff rather than the editor one. */
  const stalled = useStalled(
    used,
    integration === "codex" ? TERMINAL_WAIT_MS : EDITOR_WAIT_MS,
  );

  useEffect(() => {
    if (used && !connected) onContinue();
  }, [used, connected, onContinue]);

  /* The lead's second sentence carries what the "other" guide dropped when its
     provider-key step went: nothing here asks for a model provider key. True of
     all four integrations — Claude Code and Codex keep their own credential,
     LangChain and the generic guide pass yours through. */
  return (
    <WizardFrame
      step={1}
      lead="Send your agent's traffic through the gateway. Your provider key doesn't change."
    >
      <IntegrationSnippets
        type={integration}
        agentId={agentId}
        onCopy={() => trackButtonPressed("Copy code")}
        showRestartNote={false}
      />

      {/* The wait, said out loud: the reader has pasted a snippet into another window and
          has nothing here telling them this page is watching for the result. */}
      <AgentTrafficCard
        used={used}
        stalled={stalled}
        hint={
          stalled
            ? "Nothing has reached the gateway yet — the checklist above covers what usually stops it."
            : "This appears as soon as your agent's first request reaches the gateway."
        }
        action={
          stalled && onSkip ? (
            <button
              type="button"
              onClick={() => {
                trackButtonPressed("Skip to dashboard");
                onSkip();
              }}
              className="text-[12px] font-medium underline"
              style={{ color: "var(--fg-2)" }}
            >
              Skip and go to the dashboard
            </button>
          ) : undefined
        }
      />

      {integration === "claude-code" && <GatewayLimitations />}

      <TroubleshootingChecklist integration={integration} />

      {/* `used` is a server-side latch, so leaving costs nothing: the agent lights up
          whenever its first request lands, wizard open or not. */}
      {stalled && <SupportLink />}

      {/* Only on a return visit: the first pass advances itself when traffic arrives. */}
      {connected && (
        <WizardCta
          className="w-full"
          onClick={onContinue}
          iconRight={<ArrowRight />}
        >
          Continue
        </WizardCta>
      )}

      {/* Not a one-way door, matching the Reconnect drawer: a reader who picked the
          snippet to look at it can still hand the job to the CLI. */}
      {onUseCli && (
        <button
          type="button"
          onClick={onUseCli}
          className="self-center text-[13px] font-medium underline"
          style={{ color: "#757f8c" }}
        >
          Use the CLI instead
        </button>
      )}

      {onBack && (
        <button
          type="button"
          onClick={onBack}
          className="mt-1 flex items-center justify-center gap-2 self-center text-[14px] font-medium"
          style={{ color: "#757f8c" }}
        >
          <ArrowLeft size={14} />
          Back
        </button>
      )}
    </WizardFrame>
  );
}

/* The reconnect drawer, where this is the whole flow rather than a wizard step:
 * the agent already has traffic, so there is nothing to wait for. */
function StandaloneConnect({
  kind,
  agentId,
  onContinue,
  ctaLabel,
  onChangeType,
}: {
  kind: AgentKind;
  agentId: string;
  onContinue: () => void;
  ctaLabel: string;
  onChangeType?: () => void;
}) {
  const integration = integrationForKind(kind);

  return (
    <StepBody title="Connect your agent" lead="Your custom snippet is ready.">
      <IntegrationSnippets
        type={integration}
        agentId={agentId}
        onCopy={() => trackButtonPressed("Copy code")}
        showRestartNote
      />

      {integration === "claude-code" && <GatewayLimitations />}

      <WizardCta
        className="w-full"
        onClick={() => {
          trackButtonPressed(ctaLabel);
          onContinue();
        }}
      >
        {ctaLabel}
      </WizardCta>

      {onChangeType && (
        <button
          type="button"
          onClick={() => {
            trackButtonPressed("Change agent type");
            onChangeType();
          }}
          className="self-start t-caption transition-colors"
          style={{ color: "var(--fg-3)" }}
        >
          Not your agent type?{" "}
          <span style={{ color: "var(--onb-link)", fontWeight: 600 }}>
            Go back
          </span>
        </button>
      )}
    </StepBody>
  );
}

/* What Claude Code gives up behind the gateway: it gates these on the base URL being
 * Anthropic's own host, so they turn off silently. Collapsed, so it can't rival the CTA. */
function GatewayLimitations() {
  return (
    <details
      className="rounded-[5px] border p-3"
      style={{ borderColor: "var(--onb-track)" }}
    >
      <summary
        className="cursor-pointer text-[13px] font-medium"
        style={{ color: "var(--fg-2)" }}
      >
        What changes when Claude Code runs through the gateway
      </summary>
      <ul className="mt-2 flex list-disc flex-col gap-1.5 pl-4 text-[12px] text-[var(--text-muted)]">
        <li>
          <span className="font-medium">
            Remote control, Dispatch and Channels stop working.
          </span>{" "}
          They run over Claude&apos;s own control-plane connection rather than
          model traffic, so no gateway can carry them.
        </li>
        <li>
          <span className="font-medium">
            Anthropic-hosted extras are unavailable:
          </span>{" "}
          Claude&apos;s hosted MCP connectors and{" "}
          <InlineCode>/feedback</InlineCode> bug reports. Web search and
          WebFetch keep working.
        </li>
        <li>
          <span className="font-medium">
            Team and cross-device memory sync stops.
          </span>{" "}
          Memory shared across your team or your own machines no longer syncs,
          with no error. Local <InlineCode>CLAUDE.md</InlineCode> and project
          memory are unaffected.
        </li>
        <li>
          <span className="font-medium">
            Settings your admin pushes from the Claude Console stop applying.
          </span>{" "}
          Claude Code fetches those from Anthropic, so behind the gateway it
          silently ignores them: the permission rules and usage limits your org
          set are dropped, and anything they restricted falls back to Claude
          Code&apos;s own defaults, which allow <em>more</em>. Ship the same
          rules as a local{" "}
          <a
            href="https://code.claude.com/docs/en/settings"
            target="_blank"
            rel="noopener noreferrer"
            className="no-underline hover:underline"
            style={{ color: "var(--fg-1)" }}
          >
            <InlineCode>managed-settings.json</InlineCode>
          </a>{" "}
          via MDM instead.
        </li>
      </ul>
    </details>
  );
}
